/**
 * Controlled row-selection state for DataTables (stable ID compare).
 */

import * as React from 'react';

/** Order-sensitive id compare so `selectAll` does not reset an identical selection. */
function sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((id, index) => id === b[index]);
}

/** Controlled DataTable row selection (`toggle` / `selectAll` / `pruneTo`). */
export function useTableSelection() {
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const toggle = React.useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id],
    );
  }, []);

  const selectAll = React.useCallback((ids: string[]) => {
    setSelectedIds((prev) => (sameIds(prev, ids) ? prev : ids));
  }, []);

  const clear = React.useCallback(() => {
    setSelectedIds((prev) => (prev.length === 0 ? prev : []));
  }, []);

  const isSelected = React.useCallback((id: string) => selectedIds.includes(id), [selectedIds]);

  const pruneTo = React.useCallback((validIds: string[]) => {
    const validSet = new Set(validIds);
    setSelectedIds((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.filter((id) => validSet.has(id));
      return sameIds(prev, next) ? prev : next;
    });
  }, []);

  return React.useMemo(
    () => ({ selectedIds, setSelectedIds, toggle, selectAll, clear, isSelected, pruneTo }),
    [selectedIds, toggle, selectAll, clear, isSelected, pruneTo],
  );
}
